import { Dispatch, SetStateAction } from 'react';
import {
	FieldErrors,
	FieldValues,
	UseFormRegister,
	UseFormSetValue,
	UseFormWatch,
} from 'react-hook-form';
import { Tab } from '@headlessui/react';
import { Typography } from '@/components/common';
import { PaymentCard } from './PaymentCard';
import { PaymentTab } from './PaymentTab';
import { PaymentType } from '@/interfaces';
import * as React from 'react';
import { OptionType } from '@/components/screens/dashboard/settings/PaymentDetails';

interface Props {
	selectedIndex: number;
	setSelectedIndex: Dispatch<SetStateAction<number>>;
	errors: FieldErrors<FieldValues>;
	watch: UseFormWatch<FieldValues>;
	register: UseFormRegister<FieldValues>;
	setValue: UseFormSetValue<FieldValues>;
	options: OptionType[];
	paymentSelected: PaymentType | null;
	setPaymentSelected: (value: PaymentType) => void;
	setDisabledButtons: (value: boolean) => void;
	setPaymentToDelete: (value: PaymentType) => void;
	showDeleteModal: () => void;
}

export const PaymentTabs: React.FC<Props> = ({
	selectedIndex,
	setSelectedIndex,
	errors,
	watch,
	register,
	setValue,
	options,
	paymentSelected,
	setPaymentSelected,
	setDisabledButtons,
	setPaymentToDelete,
	showDeleteModal,
}) => {
	// check the empty forms to put disable the buttons
	React.useEffect(() => {
		if (selectedIndex !== -1) {
			let isEmpty = !options[selectedIndex]?.isAdded;
			const type = options[selectedIndex].name;

			if (type === 'paypal' && watch('paypalEmail')) {
				isEmpty = false;
			} else if (
				type === 'venmo' &&
				watch('accountName') &&
				watch('accountAlias') &&
				watch('phoneNumber')
			) {
				isEmpty = false;
			} else {
				isEmpty = true;
			}
			setDisabledButtons(isEmpty);
		}
	}, [
		selectedIndex,
		watch('paypalEmail'),
		watch('accountName'),
		watch('accountAlias'),
		watch('phoneNumber'),
	]);

	return (
		<>
			<Tab.Group
				vertical
				defaultIndex={options.findIndex(
					(item) => item.name === paymentSelected
				)}
				selectedIndex={selectedIndex}
				onChange={setSelectedIndex}
			>
				<div className="flex gap-10 pt-6">
					<Tab.List
						className={
							'flex-1 w-1/2 max-w-md flex flex-col items-start space-y-4'
						}
					>
						<Typography type="semibold-14" className="mb-2">
							Select your preferred payment method
						</Typography>
						{options.map((option, index) => (
							<React.Fragment key={'tab-payment-' + option.name}>
								<PaymentTab
									index={index}
									option={option}
									register={register}
									selectedIndex={selectedIndex}
									setSelectedIndex={setSelectedIndex}
									setPaymentSelected={setPaymentSelected}
									paymentSelected={paymentSelected}
								/>
							</React.Fragment>
						))}
					</Tab.List>
					<Tab.Panels className={'flex-1 w-1/2'}>
						{options.map((option, index) => {
							const Component = option.component;
							return (
								<Tab.Panel
									key={'form-payment-' + option.name}
									hidden={index !== selectedIndex}
								>
									<PaymentCard
										name={option.name as PaymentType}
										showModal={() => {
											setPaymentToDelete(option.name);
											showDeleteModal();
										}}
										isAdded={option.isAdded}
									>
										<Component
											watch={watch}
											errors={errors}
											setValue={setValue}
											register={register}
										/>
									</PaymentCard>
								</Tab.Panel>
							);
						})}
					</Tab.Panels>
				</div>
			</Tab.Group>
		</>
	);
};
